import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { CreateEntraAppRequest, EntraApp, SetEntraAppSecretRequest, UpdateEntraAppRequest } from '@models/csp';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnUrlHandler,
  deleteEntity,
  findAndReplace,
  getAll,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { EntraAppsService } from './entra-apps.service';

type EntraAppsState = {
  entraApps: EntraApp[];
  selectedEntraAppId: string | null;
  isLoading: boolean;
  isEntraAppCreateLoading: boolean;

  isSetSecretLoading: boolean;
  savedSecret: SetEntraAppSecretRequest | null;
};

const initialState: EntraAppsState = {
  entraApps: [],
  selectedEntraAppId: null,
  isLoading: false,
  isEntraAppCreateLoading: false,

  isSetSecretLoading: false,
  savedSecret: null,
};

const entraAppsDictionary: StateDictionary<EntraApp> = {
  entityList: 'entraApps',
  listLoading: 'isLoading',
  createLoading: 'isEntraAppCreateLoading',
  selectedEntity: '',
  selectedEntityLoading: '',
  entityId: 'id',
  sortFn: (a: EntraApp, b: EntraApp) => a.name.localeCompare(b.name),
};

export const EntraAppsStore = signalStore(
  withState(initialState),
  withDevtools('entra-apps-store'),
  withComputed(store => ({
    selectedEntraApp: computed(() => store.entraApps().find(entraApp => entraApp.id === store.selectedEntraAppId()) || null),
  })),
  withMethods(
    (store, entraAppsService = inject(EntraAppsService), router = inject(Router), notificationsService = inject(NotificationsService)) => {
      createCustomStateSetOnUrlHandler<EntraAppsState>('/csp/entra-apps', { selectedEntraAppId: null })(store);

      const repositoryMethods = {
        getAll: getAll<EntraApp>({
          store,
          service: entraAppsService,
          dictionary: entraAppsDictionary,
        }),

        addEntraApp: addEntity<EntraApp, CreateEntraAppRequest>({
          store,
          service: entraAppsService,
          dictionary: entraAppsDictionary,
          onSuccessCallback: (newEntraApp: EntraApp) => {
            patchState(store, { selectedEntraAppId: newEntraApp.id });
            router.navigate(['csp', 'entra-apps', newEntraApp.id, 'details']);
          },
        }),

        updateEntraApp: updateEntity<EntraApp, UpdateEntraAppRequest>({
          store,
          service: entraAppsService,
          dictionary: entraAppsDictionary,
          onSuccessCallback: (updatedEntraApp: EntraApp) => {
            patchState(store, { selectedEntraAppId: updatedEntraApp.id });
          },
        }),

        deleteEntraApp: deleteEntity<EntraApp>({
          store,
          service: entraAppsService,
          dictionary: entraAppsDictionary,
          onSuccessCallback: () => {
            patchState(store, {
              selectedEntraAppId: null,
            });
            router.navigate(['csp', 'entra-apps']);
          },
        }),

        setSecret: rxMethod<{ id: string; dto: SetEntraAppSecretRequest }>(
          pipe(
            tap(() => patchState(store, { isSetSecretLoading: true })),
            switchMap(({ id, dto }) =>
              entraAppsService.setSecret(id, dto).pipe(
                tap(entraApp => {
                  patchState(store, {
                    entraApps: findAndReplace<EntraApp>(store.entraApps(), entraApp, entraApp => id === entraApp.id),
                    isSetSecretLoading: false,
                    savedSecret: { secret: entraApp.secret, expiresAt: entraApp.expiresAt },
                  });
                  repositoryMethods.getAll();
                  notificationsService.showNotification('Secret set successfully.');
                }),
                catchError(() => {
                  patchState(store, { isSetSecretLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationActions = {
        selectEntraApp: (id: string): void => {
          if (!store.entraApps().length && !store.isLoading()) repositoryMethods.getAll();
          patchState(store, { selectedEntraAppId: id });
        },

        goToEntraAppActions: async (id: string): Promise<boolean> => {
          const result = await router.navigate(['csp', 'entra-apps', id]);

          if (!result) return false;
          navigationActions.selectEntraApp(id);
          return true;
        },

        resetState: (state: Partial<EntraAppsState>) => patchState(store, state),
      };

      return {
        ...repositoryMethods,
        ...navigationActions,
      };
    },
  ),
);

export interface EntraAppsStoreType {
  readonly entraApps: Signal<EntraApp[]>;
  readonly selectedEntraAppId: Signal<string | null>;
  readonly isLoading: Signal<boolean>;
  readonly isEntraAppCreateLoading: Signal<boolean>;
  readonly isSetSecretLoading: Signal<boolean>;
  readonly savedSecret: Signal<SetEntraAppSecretRequest | null>;

  readonly selectedEntraApp: Signal<EntraApp | null>;

  readonly getAll: () => void;
  readonly addEntraApp: (request: CreateEntraAppRequest) => void;
  readonly updateEntraApp: (input: { id: string; dto: UpdateEntraAppRequest }) => void;
  readonly deleteEntraApp: (id: string) => void;
  readonly selectEntraApp: (id: string) => void;
  readonly goToEntraAppActions: (id: string) => Promise<boolean>;
  readonly setSecret: (params: { id: string; dto: SetEntraAppSecretRequest }) => void;
  readonly resetState: (state: Partial<EntraAppsState>) => void;
}
